import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import momentTimezonePlugin from '@fullcalendar/moment-timezone';
import allLocales from '@fullcalendar/core/locales-all';
import { useDateTime } from '../../contexts/DateTimeContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useTask } from '../../contexts/TaskContext';
import { useEntitlements } from '../../hooks/useEntitlements';
import { useCalendarOccurrences } from '../../lib/occurrences';
import {
  useFocusScheduledEventsQuery, useGoogleCalendarEventsQuery, useHabitScheduledEventsQuery, useSettingsQuery,
} from '../../hooks/queries';
import {
  PlannerCalendarStyles, buildCalendarEvents, createEventRenderer, getCompletedColors, getPriorityColors, getSlotRange,
} from '../planner/plannerCalendar';

const EMPTY = Object.freeze([]);
const SLOT_HEIGHT = 24;  // the Planner's smallest slot
const HEIGHT = 560;
// The calendar sits inside Home's Schedule card: no frame of its own, and the whole column is
// today, so the Planner's honey tint for today would only colour the card (the user, 2026-10-30).
const BOX = 'home-day-calendar overflow-hidden';
const HOME_STYLES = '.home-day-calendar .fc { --fc-today-bg-color: transparent; }';

/**
 * Today in the Planner's calendar (the same events, colours and drawing, read-only):
 * time-blocked tasks, Google Calendar events, habits and focus blocks. A task opens its detail.
 */
const DayCalendar = ({ tasks, projects }) => {
  const { t } = useTranslation();
  const { formatTime, timeFormat, timezone, timezoneOffsetAdjust, language } = useDateTime();
  const calendarLocale = { zh: 'zh-cn', ta: 'ta-in' }[language] || language;
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const { has } = useEntitlements();
  const { openTaskDetail } = useTask();
  const { data: settings } = useSettingsQuery();
  const { data: googleEvents = EMPTY } = useGoogleCalendarEventsQuery(undefined, undefined, { enabled: has('google_calendar') });
  const { data: habitEvents = EMPTY } = useHabitScheduledEventsQuery();
  const { data: focusEvents = EMPTY } = useFocusScheduledEventsQuery();

  const getProjectName = useCallback((projectId) => {
    const project = (projects || []).find((p) => p.id === projectId);
    if (!project) return null;
    return project.team_name ? `${project.team_name} / ${project.name}` : project.name;
  }, [projects]);
  const { occurrences, onDatesSet } = useCalendarOccurrences();
  const events = useMemo(() => buildCalendarEvents({
    visibleTasks: tasks, googleEvents, habitEvents, focusEvents,
    priorityColors: getPriorityColors(isDark), completedColors: getCompletedColors(isDark), getProjectName,
    occurrences,
  }), [tasks, googleEvents, habitEvents, focusEvents, isDark, getProjectName, occurrences]);
  // The Planner's hours, widened so nothing on today's schedule falls outside them.
  const { slotMinTime, slotMaxTime } = useMemo(() => {
    const range = getSlotRange(settings?.working_hours, timezoneOffsetAdjust);
    const today = new Date().toDateString();
    const hours = events.filter((e) => e.start && new Date(e.start).toDateString() === today).flatMap((e) => {
      const start = new Date(e.start);
      const end = e.end ? new Date(e.end) : start;
      return [start.getHours(), end.getHours() + (end.getMinutes() ? 1 : 0)];
    });
    const min = Math.min(range.minHour, ...hours);
    const max = Math.min(24, Math.max(range.maxHour, ...hours.map((h) => h + 1)));
    const pad = (h) => `${String(h).padStart(2, '0')}:00:00`;
    return { slotMinTime: pad(Math.max(0, min)), slotMaxTime: pad(max) };
  }, [settings, timezoneOffsetAdjust, events]);
  const renderEventContent = createEventRenderer({ formatTime, isDark, t });
  const scrollTime = `${String(Math.max(0, new Date().getHours() - 1)).padStart(2, '0')}:00:00`;

  return (
    <div className={BOX} data-testid="home-day-calendar">
      <PlannerCalendarStyles isDark={isDark} slotHeight={SLOT_HEIGHT} />
      <style>{HOME_STYLES}</style>
      <FullCalendar
        plugins={[timeGridPlugin, momentTimezonePlugin]}
        initialView="timeGridDay"
        timeZone={timezone}
        locales={allLocales}
        locale={calendarLocale}
        headerToolbar={false}
        dayHeaders={false}
        events={events}
        editable={false}
        nowIndicator
        slotMinTime={slotMinTime}
        slotMaxTime={slotMaxTime}
        slotDuration="00:30:00"
        scrollTime={scrollTime}
        allDaySlot={false}
        height={HEIGHT}
        eventContent={renderEventContent}
        datesSet={onDatesSet}
        eventClick={(info) => {
          const task = info.event.extendedProps.task;
          if (task) openTaskDetail(task.id);
        }}
        slotLabelFormat={{ hour: 'numeric', minute: '2-digit', hour12: timeFormat === '12h' }}
      />
    </div>
  );
};

// On a phone the calendar comes after the day's tasks, a long way down, and drawing it
// was the biggest part of opening Home: it is drawn when it is a screen away from view.
const HomeDayCalendar = (props) => {
  const boxRef = useRef(null);
  const [near, setNear] = useState(() => typeof IntersectionObserver === 'undefined');
  useEffect(() => {
    const box = boxRef.current;
    if (near || !box) return undefined;
    let root = box.parentElement;
    while (root && !/(auto|scroll)/.test(getComputedStyle(root).overflowY)) root = root.parentElement;
    const seen = new IntersectionObserver(([e]) => { if (e.isIntersecting) setNear(true); },
      { root, rootMargin: '100% 0px' });
    seen.observe(box);
    return () => seen.disconnect();
  }, [near]);
  if (near) return <DayCalendar {...props} />;
  return <div ref={boxRef} className={BOX} style={{ height: HEIGHT }} data-testid="home-day-calendar" />;
};

export default HomeDayCalendar;
